import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'reactstrap';
import { IApiActionResult } from 'src/~api/ApiActionResult';
import {
    applyNLogConfigAsync,
    getNLogConfigAsync,
    INLogConfigResult,
    resetNLogConfigAsync,
    validateNLogConfigAsync
} from './~api/actions';
import { LOGGING_SERVICES } from './~api/apiRoutes';

import './LoggingMain.css';

const CUSTOMER_RULE_EXAMPLE = `"00_CustomerDebug": {
  "logger": "ItSymphony.Chat.Telegram*",
  "minLevel": "Debug",
  "maxLevel": "Debug",
  "writeTo": "main_logger",
  "filterDefaultAction": "Ignore",
  "filters": {
    "whenEqual": { "layout": "\${scopeproperty:CustomerId}", "compareTo": "<CustomerId>", "action": "Log" }
  }
}`;

const LoggingMain = () => {
    const { t } = useTranslation();
    const [service, setService] = useState(LOGGING_SERVICES[0].route);
    const [config, setConfig] = useState('');
    const [savedConfig, setSavedConfig] = useState('');
    const [errors, setErrors] = useState<string[]>([]);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [isOverride, setIsOverride] = useState(false);

    const showResult = (resultErrors: string[], successMessage: string) => {
        setErrors(resultErrors);
        setMessage(resultErrors.length ? '' : successMessage);
    };

    const run = async (action: Promise<IApiActionResult<INLogConfigResult>>) => {
        setBusy(true);
        const result = await action;
        setBusy(false);
        if (!result.isSuccess || !result.value) {
            result.tryShowErrorToastr();
            return undefined;
        }
        return result.value;
    };

    const load = useCallback(async () => {
        const result = await run(getNLogConfigAsync(service));
        const loaded = result?.config ?? '';
        setConfig(loaded);
        setSavedConfig(loaded);
        setIsOverride(result?.isOverride ?? false);
        showResult(result?.errors ?? [], '');
    }, [service]);

    useEffect(() => {
        load();
    }, [load]);

    const validate = async () => {
        const result = await run(validateNLogConfigAsync(service, config));
        if (result) {
            showResult(result.errors, t('logging.valid'));
        }
    };

    const apply = async () => {
        const result = await run(applyNLogConfigAsync(service, config));
        if (result) {
            showResult(result.errors, t('logging.applied'));
            setIsOverride(result.isOverride);
            if (!result.errors.length) {
                setSavedConfig(config);
            }
        }
    };

    const reset = async () => {
        const result = await run(resetNLogConfigAsync(service));
        if (result) {
            setConfig(result.config);
            setSavedConfig(result.config);
            setIsOverride(result.isOverride);
            showResult(result.errors, t('logging.resetDone'));
        }
    };

    const revert = () => {
        setConfig(savedConfig);
        showResult([], '');
    };

    const hasChanges = config !== savedConfig;

    return (
        <div className="logging-main main-section">
            <div className="logging-main__header">{t('logging.title')}</div>

            <div className="logging-main__toolbar">
                <div className="logging-main__service">
                    <label>{t('logging.service')}</label>
                    <Input type="select" className="browser-default" value={service} disabled={busy} onChange={e => setService(e.target.value)}>
                        {LOGGING_SERVICES.map(s => <option key={s.route} value={s.route}>{s.name}</option>)}
                    </Input>
                </div>
                <Button onClick={validate} disabled={busy}>{t('logging.validate')}</Button>
                <Button onClick={apply} disabled={busy || !hasChanges}>{t('logging.apply')}</Button>
                <Button className="logging-main__cancel" onClick={revert} disabled={busy || !hasChanges}>{t('common.cancel')}</Button>
                <Button className="logging-main__reset" onClick={reset} disabled={busy}>{t('logging.reset')}</Button>
            </div>

            <div className="logging-main__hint">{t('logging.hint')}</div>

            {isOverride && <div className="logging-main__override">{t('logging.overrideActive')}</div>}

            {message && <div className="logging-main__message">{message}</div>}
            {errors.length > 0 && (
                <div className="logging-main__errors">
                    {errors.map((error, index) => <div key={index}>{error}</div>)}
                </div>
            )}

            <textarea
                className="logging-main__editor"
                value={config}
                spellCheck={false}
                onChange={e => setConfig(e.target.value)} />

            <details className="logging-main__example">
                <summary>{t('logging.customerExample')}</summary>
                <pre>{CUSTOMER_RULE_EXAMPLE}</pre>
            </details>
        </div>
    );
}

export default LoggingMain;
